import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { api } from "../../../app/Api/ApiSlice";
import { toast } from "react-toastify";
import moment from "moment";
import officeImg from "../../../assets/office.png";
import UserImage from "../../../assets/user.png";
import { Country } from "country-state-city";

const CreateProductAd = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { name } = useParams();

    const { ad = null, adType = "product" } = location.state || {};

    const files = useMemo(() => ad?.files || [], [ad]);

    const [focusedImg, setFocusedImg] = useState(null);
    const [allCountries, setAllCountries] = useState([]);
    const [selectedCountries, setSelectedCountries] = useState([]);
    const [budget, setBudget] = useState(250);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [audience, setAudience] = useState("general");
    const [isSubmitting, setIsSubmitting] = useState(false);

    const today = new Date().toISOString().split("T")[0];

    useEffect(() => {
        if (files.length > 0) {
            setFocusedImg(files[0]);
        }
    }, [files]);

    useEffect(() => {
        const countries = Country.getAllCountries().map((c) => ({
            value: c.isoCode,
            label: c.name,
        }));
        setAllCountries(countries);
    }, []);

    useEffect(() => {
        if (!startDate || !endDate) return;

        const start = new Date(startDate);
        const end = new Date(endDate);

        if (end < start) return;

        const days =
            Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;

        setBudget(days * 250);
    }, [startDate, endDate]);

    const toggleCountry = (country) => {
        setSelectedCountries((prev) =>
            prev.some((c) => c.value === country.value)
                ? prev.filter((c) => c.value !== country.value)
                : [...prev, country]
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!ad?._id) {
            toast.error("Invalid ad reference");
            return;
        }

        if (!budget || Number(budget) <= 0) {
            toast.error("Valid daily budget required");
            return;
        }

        if (!startDate || !endDate) {
            toast.error("Start and end dates required");
            return;
        }

        if (endDate < startDate) {
            toast.error("End date cannot be before start date");
            return;
        }

        try {
            setIsSubmitting(true);

            const response = await api.post(
                `/stores/${name}/manage/promotions/products`,
                {
                    adId: ad._id,
                    selectedCountries: selectedCountries.map(
                        (c) => c.value
                    ),
                    budget: Number(budget),
                    audience,
                    startDate,
                    endDate,
                    entity: ad.store,
                    adType,
                }
            );

            const savedAd = response?.data?.ad || response?.data;

            if (!savedAd?._id) {
                toast.error("Ad not saved");
                return;
            }

            toast.success("Ad saved. Proceed to payment.");

            navigate(
                `/b/manage/${name}/promotions/${savedAd._id}/payments`,
                { state: { ad: savedAd } }
            );
        } catch (error) {
            toast.error(
                error?.response?.data?.message ||
                    "Ad creation failed"
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!ad) {
        return (
            <div className="create-ad-page">
                <p>Invalid ad context.</p>
            </div>
        );
    }

    return (
        <div>
            <div className="create-ad-title">
                <h3>Promote This Product</h3>
            </div>

            <div className="create-ad-page">
                <form
                    onSubmit={handleSubmit}
                    className="create-ad-form"
                >
                    <div className="ad-content">
                        <div className="post">
                            <div className="org-post">
                                <div className="org-post-header">
                                    <div className="profile-details">
                                        <img
                                            src={
                                                ad.store?.profileImage ||
                                                officeImg
                                            }
                                            alt=""
                                            className="userpost-profile-img"
                                        />
                                        <div className="post-data-details">
                                            <p className="post-profile-name">
                                                {ad.store?.name}
                                            </p>
                                            <div className="post-dates">
                                                <p>
                                                    {moment(
                                                        ad.createdAt
                                                    ).fromNow()}
                                                </p>
                                                <p>Audience</p>
                                            </div>
                                        </div>
                                    </div>

                                    {ad.createdBy?._id && (
                                        <Link
                                            to={`/user/${ad.createdBy._id}`}
                                        >
                                            <div className="org-post-user">
                                                <div className="org-post-userimg">
                                                    <img
                                                        src={
                                                            ad.createdBy
                                                                ?.profilePic ||
                                                            UserImage
                                                        }
                                                        alt=""
                                                    />
                                                </div>
                                                <div className="org-post-username">
                                                    <h3>
                                                        {
                                                            ad.createdBy
                                                                ?.firstname
                                                        }{" "}
                                                        {
                                                            ad.createdBy
                                                                ?.lastname
                                                        }
                                                    </h3>
                                                </div>
                                            </div>
                                        </Link>
                                    )}
                                </div>

                                <div className="post-body">
                                    <p>{ad.description}</p>

                                    {files.length > 0 && (
                                        <div className="postcard-images">
                                            {files[0]?.type ===
                                                "image" &&
                                                focusedImg && (
                                                    <div className="post-images">
                                                        <div className="focused-image">
                                                            <img
                                                                src={
                                                                    focusedImg.url
                                                                }
                                                                alt="focused"
                                                            />
                                                        </div>
                                                        <div className="image-selector">
                                                            {files.map(
                                                                (
                                                                    file,
                                                                    i
                                                                ) => (
                                                                    <div
                                                                        key={
                                                                            i
                                                                        }
                                                                        onClick={() =>
                                                                            setFocusedImg(
                                                                                file
                                                                            )
                                                                        }
                                                                    >
                                                                        <img
                                                                            src={
                                                                                file.url
                                                                            }
                                                                            alt="thumb"
                                                                            className="ad-image-select"
                                                                        />
                                                                    </div>
                                                                )
                                                            )}
                                                        </div>
                                                    </div>
                                                )}

                                            {files[0]?.type ===
                                                "video" && (
                                                <div className="video-container">
                                                    <video controls>
                                                        <source
                                                            src={
                                                                files[0]
                                                                    .url
                                                            }
                                                            type="video/mp4"
                                                        />
                                                    </video>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Dates *</label>

                        <input
                            type="date"
                            min={today}
                            value={startDate}
                            required
                            onChange={(e) =>
                                setStartDate(
                                    e.target.value
                                )
                            }
                        />

                        <input
                            type="date"
                            min={startDate || today}
                            value={endDate}
                            required
                            onChange={(e) =>
                                setEndDate(
                                    e.target.value
                                )
                            }
                        />
                    </div>

                    <div className="form-group">
                        <label>Target Audience</label>

                        <select
                            value={audience}
                            onChange={(e) =>
                                setAudience(
                                    e.target.value
                                )
                            }
                        >
                            <option value="general">
                                General
                            </option>
                            <option value="youth">
                                Youth
                            </option>
                            <option value="professionals">
                                Professionals
                            </option>
                            <option value="business">
                                Business
                            </option>
                        </select>

                        <div className="countries">
                            {allCountries.map((c) => (
                                <button
                                    type="button"
                                    key={c.value}
                                    className={
                                        selectedCountries.some(
                                            (s) =>
                                                s.value ===
                                                c.value
                                        )
                                            ? "country active"
                                            : "country"
                                    }
                                    onClick={() =>
                                        toggleCountry(c)
                                    }
                                >
                                    {c.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="payment-section">
                        <h4>Payment</h4>
                        <p>
                            Amount:{" "}
                            <strong>
                                Ksh {budget}
                            </strong>
                        </p>
                    </div>

                    <div className="form-actions">
                        <button
                            type="submit"
                            disabled={isSubmitting}
                        >
                            {isSubmitting
                                ? "Processing..."
                                : "Save"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateProductAd;